import { labelOf, MTSS_CATEGORIES, type WorkerPosition } from '@facture/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  BriefcaseIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  ShieldCheckIcon,
  Trash2Icon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirm } from '@/components/ui/confirm'
import { columnHelper, DataTable } from '@/components/ui/data-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClearFilters, FilterBar, FilterSearch, FilterTabs } from '@/components/ui/filter-bar'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { invalidatePayroll, positionsQuery } from '@/features/payroll/api'
import { fmtCol, PayrollDisclaimer } from '@/features/payroll/payroll-ui'
import { PositionDialog } from '@/features/payroll/position-dialog'
import { api } from '@/lib/api'
import { useCan } from '@/lib/session'

export const Route = createFileRoute('/_app/projects/positions')({
  staticData: { crumb: 'Puestos' },
  component: PositionsPage,
})

const col = columnHelper<WorkerPosition>()
const normalize = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

function PositionsPage() {
  const canWrite = useCan('payroll:write')
  const queryClient = useQueryClient()
  const list = useQuery(positionsQuery)
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active')
  const [text, setText] = useState('')
  const [editing, setEditing] = useState<WorkerPosition | 'new' | null>(null)

  const rows = useMemo(() => {
    const q = normalize(text.trim())
    return (list.data ?? []).filter(
      (p) =>
        (status === 'all' || (status === 'active' ? p.isActive : !p.isActive)) &&
        (!q || normalize(p.name).includes(q)),
    )
  }, [list.data, status, text])

  const remove = (p: WorkerPosition) =>
    confirm({
      title: `¿Eliminar el puesto «${p.name}»?`,
      description:
        p.workerCount || p.assignmentCount
          ? 'Tiene trabajadores o asignaciones: se desactiva y conserva su historia.'
          : 'No tiene trabajadores ni asignaciones.',
      confirmLabel: p.workerCount || p.assignmentCount ? 'Desactivar' : 'Eliminar',
      destructive: true,
      onConfirm: async () => {
        const result = await api.delete<{ deleted: boolean }>(`/worker-positions/${p.id}`)
        toast.success(result.deleted ? 'Puesto eliminado' : 'Puesto desactivado')
        invalidatePayroll(queryClient)
      },
    })

  // biome-ignore lint/correctness/useExhaustiveDependencies: las acciones (confirmar, editar) no cambian el render de las columnas
  const columns = useMemo(
    () => [
      col.accessor('name', {
        header: 'Puesto',
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <span className="hidden size-9 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground sm:flex">
              <BriefcaseIcon className="size-4" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{row.original.name}</span>
                {row.original.excludedFromLimits && (
                  <Badge variant="info">
                    <ShieldCheckIcon /> Art. 143
                  </Badge>
                )}
              </div>
              <div className="truncate text-muted-foreground text-xs">
                {row.original.mtssCategory
                  ? labelOf(MTSS_CATEGORIES, row.original.mtssCategory)
                  : 'Sin categoría MTSS'}
              </div>
            </div>
          </div>
        ),
      }),
      col.accessor('dailyRate', {
        header: 'Jornal',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="font-medium">{fmtCol(getValue())}</span>,
      }),
      col.accessor('hourlyRate', {
        header: 'Hora diurna',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ row }) =>
          row.original.hourlyRate ? (
            fmtCol(row.original.hourlyRate)
          ) : (
            <span className="text-muted-foreground">{fmtCol(row.original.dailyRate / 8)} · jornal ÷ 8</span>
          ),
      }),
      col.accessor('workerCount', {
        header: 'Trabajadores',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
      }),
      col.accessor('assignmentCount', {
        header: 'En proyectos',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
      }),
      col.display({
        id: 'actions',
        header: () => <span className="sr-only">Acciones</span>,
        meta: { align: 'right', className: 'w-14' },
        cell: ({ row }) =>
          canWrite ? (
            <div className="flex justify-end">
              {!row.original.isActive && (
                <Badge variant="neutral" className="mr-2">
                  Inactivo
                </Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon-xs" aria-label="Acciones">
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem onSelect={() => setEditing(row.original)}>
                    <PencilIcon /> Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem destructive onSelect={() => remove(row.original)}>
                    <Trash2Icon /> Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null,
      }),
    ],
    [canWrite],
  )

  const filtered = !!text || status !== 'active'
  return (
    <Page>
      <PageHeader
        title="Puestos"
        description="Jornal de referencia de cada puesto y si está excluido de la limitación de jornada (Art. 143)."
        actions={
          canWrite && (
            <Button onClick={() => setEditing('new')}>
              <PlusIcon /> Nuevo puesto
            </Button>
          )
        }
      />
      <PayrollDisclaimer className="mb-4" />
      <DataTable
        columns={columns}
        data={list.isLoading ? undefined : rows}
        loading={list.isFetching}
        getRowId={(p) => p.id}
        onRowClick={canWrite ? (p) => setEditing(p) : undefined}
        isRowMuted={(p) => !p.isActive}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Estado"
              value={status}
              onValueChange={setStatus}
              options={[
                { value: 'active', label: 'Activos' },
                { value: 'inactive', label: 'Inactivos' },
                { value: 'all', label: 'Todos' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por puesto…" />
            <ClearFilters
              active={filtered}
              onClear={() => {
                setText('')
                setStatus('active')
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={BriefcaseIcon}
            title={filtered ? 'Sin puestos con esos filtros' : 'Todavía no hay puestos'}
            description={
              filtered
                ? 'Probá con otro estado o limpiá la búsqueda.'
                : 'Creá los puestos de la obra (peón, albañil, maestro de obras…) con su jornal de referencia.'
            }
            action={
              canWrite &&
              !filtered && (
                <Button onClick={() => setEditing('new')}>
                  <PlusIcon /> Nuevo puesto
                </Button>
              )
            }
          />
        }
      />
      <PositionDialog value={editing} onClose={() => setEditing(null)} />
    </Page>
  )
}
